import { userFacingError } from '../utils/userFacingError';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Platform } from '../types';
import { StorageService } from '../services/storageService';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Textarea } from './ui/Textarea';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { 
  CloudArrowUpIcon,
  CpuChipIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

export interface GeneratedImage {
  url: string;
  storageUrl: string;
  selected: boolean;
  caption: string;
}

interface ArticleImageSelectorProps {
  // Aktueller Image-Type
  imageType: 'eigenes' | 'ki' | 'redaktion';
  onImageTypeChange: (type: 'eigenes' | 'ki' | 'redaktion') => void;
  
  // Bild-Daten
  imageStorageUrl: string;
  imageSource: string;
  imageSourceUrl: string;
  imageCaption: string;
  imageCaptionFromPrompt?: string;
  imagePrompt: string;
  
  // Upload-Datei (für eigenes Bild)
  uploadedFile?: File;
  
  // Callbacks
  onImageStorageUrlChange: (url: string) => void;
  onImageSourceChange: (source: string) => void;
  onImageSourceUrlChange: (url: string) => void;
  onImageCaptionChange: (caption: string) => void;
  onImagePromptChange: (prompt: string) => void;
  onUploadedFileChange: (file: File | undefined) => void;
  
  // Platform (für KI-Generation)
  platform: Platform | null;
  
  // Optional: Cachedmetadata für automatische Bildunterschrift
  cachedMetadata?: any;
  
  // Optional: Loading-State nach außen kommunizieren
  onLoadingChange?: (isLoading: boolean) => void;
}

export const ArticleImageSelector: React.FC<ArticleImageSelectorProps> = ({
  imageType,
  onImageTypeChange,
  imageStorageUrl,
  imageSource,
  imageSourceUrl,
  imageCaption,
  imageCaptionFromPrompt,
  imagePrompt,
  uploadedFile,
  onImageStorageUrlChange,
  onImageSourceChange,
  onImageSourceUrlChange,
  onImageCaptionChange,
  onImagePromptChange,
  onUploadedFileChange,
  platform,
  cachedMetadata,
  onLoadingChange
}) => {
  const [isLoading, setIsLoading] = useState(false);
  
  // Loading-State nach außen kommunizieren
  React.useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);
  const [previousImageType, setPreviousImageType] = useState<'eigenes' | 'ki' | 'redaktion'>(imageType);

  // Beim Wechsel des imageType die entsprechenden Standard-Daten setzen
  React.useEffect(() => {
    // Nur wenn sich der imageType tatsächlich geändert hat
    if (imageType !== previousImageType) {
      setPreviousImageType(imageType);

      if (imageType === 'redaktion') {
        // Standard-Daten für Redaktion (wie im Wizard)
        onImageStorageUrlChange('https://appwrite.e-redaktion.org/v1/storage/buckets/article-images/files/68e4ee76002eac11ff3e/view?project=689c6acc001bcf54fcdc');
        onImageSourceChange('Redaktion');
        onImageSourceUrlChange('https://www.google.de');
        onImageCaptionChange(imageCaptionFromPrompt || imageCaption || imagePrompt || 'Bild von der Redaktion ausgewählt');
        onUploadedFileChange(undefined);
      } else if (imageType === 'eigenes') {
        // Für "Eigenes Bild" - Felder leeren (User muss Bild hochladen)
        onImageStorageUrlChange('');
        onImageSourceChange('');
        onImageSourceUrlChange('');
        onImageCaptionChange('');
        onUploadedFileChange(undefined);
      } else if (imageType === 'ki') {
        // Für "KI-generiert" - Felder leeren (User muss generieren)
        onImageStorageUrlChange('');
        onImageSourceChange('');
        onImageSourceUrlChange('');
        if (imageCaptionFromPrompt) {
          onImageCaptionChange(imageCaptionFromPrompt);
        }
        onUploadedFileChange(undefined);
      }
    }
  }, [
    imageType,
    previousImageType,
    imagePrompt,
    imageCaption,
    imageCaptionFromPrompt,
    onImageStorageUrlChange,
    onImageSourceChange,
    onImageSourceUrlChange,
    onImageCaptionChange,
    onUploadedFileChange
  ]);

  // File-Upload Handler
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    try {
      const storageUrl = await StorageService.uploadImage(file);
      onImageStorageUrlChange(storageUrl);
      onUploadedFileChange(file);
      onImageSourceChange('Eigenes Bild');
      onImageSourceUrlChange('');
      onImageCaptionChange('');
      toast.success('Bild erfolgreich hochgeladen!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Fehler beim Hochladen des Bildes');
    } finally {
      setIsLoading(false);
    }
  };

  // Drag & Drop Handler
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Bitte nur Bilddateien hochladen');
      return;
    }

    setIsLoading(true);
    try {
      const storageUrl = await StorageService.uploadImage(file);
      onImageStorageUrlChange(storageUrl);
      onUploadedFileChange(file);
      onImageSourceChange('Eigenes Bild');
      onImageSourceUrlChange('');
      onImageCaptionChange('');
      toast.success('Bild erfolgreich hochgeladen!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Fehler beim Hochladen des Bildes');
    } finally {
      setIsLoading(false);
    }
  };

  // KI-Bild generieren
  const generateImage = async () => {
    if (!imagePrompt.trim()) {
      toast.error('Bitte gib einen Bild-Prompt ein');
      return;
    }

    if (!platform?.imageGenerationWebhook) {
      toast.error('Bildgenerierung ist für diese Plattform nicht konfiguriert');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🚀 Starte KI-Bildgenerierung...');
      console.log('📋 Prompt:', imagePrompt);
      console.log('🔗 Webhook:', platform.imageGenerationWebhook);
      
      // n8n Webhook für Bildgenerierung aufrufen
      const response = await fetch(platform.imageGenerationWebhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imagePrompt: imagePrompt
        }),
        // 10 Minuten Timeout für Bildgenerierung
        signal: AbortSignal.timeout(600000)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Binary-Image-Datei erhalten (wie im Original-Wizard)
      const imageBlob = await response.blob();
      console.log('✅ Bild erfolgreich generiert, Größe:', imageBlob.size, 'bytes');

      // Bild in Appwrite Storage hochladen
      const imageFile = new File([imageBlob], `ki-generated-${Date.now()}.png`, { type: 'image/png' });
      const storageUrl = await StorageService.uploadImage(imageFile);
      console.log('✅ Bild in Appwrite Storage gespeichert:', storageUrl);

      // Neues Bild zur Liste hinzufügen
      const resolvedCaption = cachedMetadata?.bild_unterschrift || imageCaption || imagePrompt;
      const newImage: GeneratedImage = {
        url: URL.createObjectURL(imageBlob),
        storageUrl: storageUrl,
        selected: true,
        caption: resolvedCaption
      };

      // Alle vorherigen Bilder als nicht ausgewählt markieren
      const updatedImages = generatedImages.map(img => ({ ...img, selected: false }));
      updatedImages.push(newImage);

      setGeneratedImages(updatedImages);
      setSelectedImageIndex(updatedImages.length - 1);

      // Formularfelder mit dem neuen Bild aktualisieren
      onImageStorageUrlChange(storageUrl);
      onImageSourceChange('e-Redaktion');
      onImageSourceUrlChange('https://www.openai.com');

      // Bildunterschrift aus gecachten Metadaten holen (sofort, ohne API-Call)
      onImageCaptionChange(resolvedCaption);

      toast.success('KI-Bild erfolgreich generiert!');
    } catch (error: any) {
      console.error('❌ Bildgenerierung Fehler:', error);
      if (error?.name === 'AbortError') {
        toast.error('Bildgenerierung-Timeout: Der Vorgang dauerte zu lange (über 10 Minuten). Bitte versuche es erneut.');
      } else if (error instanceof TypeError && error.message?.includes('fetch')) {
        toast.error('Netzwerkfehler: Bitte prüfe deine Internetverbindung und versuche es erneut.');
      } else {
        toast.error('Fehler bei der Bild-Generierung: ' + (userFacingError(error, 'Unbekannter Fehler')));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Bild auswählen
  const selectImage = (index: number) => {
    const updatedImages = generatedImages.map((img, i) => ({
      ...img,
      selected: i === index
    }));
    setGeneratedImages(updatedImages);
    setSelectedImageIndex(index);

    const selectedImage = updatedImages[index];
    onImageStorageUrlChange(selectedImage.storageUrl);
    onImageSourceChange('e-Redaktion');
    onImageSourceUrlChange('https://www.openai.com');
    onImageCaptionChange(selectedImage.caption);
  };

  return (
    <div className="space-y-6">
      {/* Aktuelles Bild Vorschau */}
      {imageStorageUrl && imageType !== 'redaktion' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aktuelles Bild
          </label>
          <div className="flex flex-col md:flex-row gap-4">
            <img
              src={StorageService.getImagePreviewUrlFromStorageUrl(imageStorageUrl, 640, 480)}
              alt="Aktuelles Artikelbild"
              className="w-full md:w-64 h-48 object-cover rounded-lg border border-gray-300"
              onError={(e) => {
                const img = e.currentTarget;
                if (img.dataset.fallback !== 'true') {
                  img.dataset.fallback = 'true';
                  img.src = imageStorageUrl;
                }
              }}
            />
            <div className="flex-1 space-y-2">
              {imageSource && (
                <div>
                  <span className="text-xs font-medium text-gray-500">Bildquelle:</span>
                  <p className="text-sm text-gray-900">{imageSource}</p>
                </div>
              )}
              {imageCaption && (
                <div>
                  <span className="text-xs font-medium text-gray-500">Bildunterschrift:</span>
                  <p className="text-sm text-gray-900">{imageCaption}</p>
                </div>
              )}
              {imagePrompt && (
                <div>
                  <span className="text-xs font-medium text-gray-500">KI-Prompt:</span>
                  <p className="text-sm text-gray-900">{imagePrompt}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {imageStorageUrl ? 'Bild ändern' : 'Artikelbild-Auswahl'}
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
              imageType === 'redaktion'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onImageTypeChange('redaktion')}
          >
            <div className="flex items-center mb-2">
              <UserGroupIcon className="w-5 h-5 text-gray-600 mr-2" />
              <h3 className="font-medium text-gray-900">Redaktion</h3>
            </div>
            <p className="text-sm text-gray-500">
              Die Redaktion wählt ein Bild aus
            </p>
          </div>
          
          <div
            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
              imageType === 'eigenes'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onImageTypeChange('eigenes')}
          >
            <div className="flex items-center mb-2">
              <CloudArrowUpIcon className="w-5 h-5 text-gray-600 mr-2" />
              <h3 className="font-medium text-gray-900">Eigenes Bild</h3>
            </div>
            <p className="text-sm text-gray-500">
              Eigenes Bild hochladen oder URL verwenden
            </p>
          </div>
          
          <div
            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
              imageType === 'ki'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => onImageTypeChange('ki')}
          >
            <div className="flex items-center mb-2">
              <CpuChipIcon className="w-5 h-5 text-gray-600 mr-2" />
              <h3 className="font-medium text-gray-900">KI-generiert</h3>
            </div>
            <p className="text-sm text-gray-500">
              KI-generierte Bilder per API erstellen
            </p>
          </div>
        </div>
      </div>

      {/* Eigenes Bild */}
      {imageType === 'eigenes' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bild hochladen
            </label>
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isLoading 
                  ? 'border-blue-300 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              }`}
              onClick={() => document.getElementById('file-input')?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="lg" />
                  <p className="text-sm text-blue-600 mt-2">
                    Bild wird hochgeladen...
                  </p>
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    Datei hierher ziehen oder klicken zum Auswählen
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    JPG, PNG, GIF, WebP (max. 30MB)
                  </p>
                </>
              )}
              <input 
                id="file-input"
                type="file" 
                className="hidden" 
                accept="image/*"
                onChange={handleFileSelect}
              />
            </div>
            
            {imageStorageUrl && uploadedFile && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hochgeladenes Bild
                </label>
                <div className="relative">
                  <img
                    src={StorageService.getImagePreviewUrlFromStorageUrl(imageStorageUrl, 640, 480)}
                    alt="Uploaded"
                    className="w-full h-48 object-contain rounded-lg border"
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.dataset.fallback !== 'true') {
                        img.dataset.fallback = 'true';
                        img.src = imageStorageUrl;
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      onImageStorageUrlChange('');
                      onUploadedFileChange(undefined);
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
            <div className="flex items-start">
              <UserGroupIcon className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
              <div>
                <p className="text-yellow-800 text-sm font-medium">
                  Warum Bildquelle, URL und Bildunterschrift nötig sind
                </p>
                <p className="text-yellow-700 text-sm mt-1">
                  <strong>Bildquelle:</strong> Wir müssen den Urheber nennen. Das kann dein eigener Name
                  oder der Name einer Person sein, die die Nutzung freigegeben hat.
                </p>
                <p className="text-yellow-700 text-sm mt-1">
                  <strong>Bildquelle-URL:</strong> Dient der Verifizierung der Quelle. Bei eigenen Fotos
                  kann das deine Webseite sein, im Zweifel auch <strong>https://www.google.com</strong>.
                </p>
                <p className="text-yellow-700 text-sm mt-1">
                  <strong>Bildunterschrift:</strong> Die Zielseite verlangt diese technisch und sie hilft
                  Leserinnen und Lesern, das Bild richtig einzuordnen.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bildquelle
            </label>
            <Input
              value={imageSource}
              onChange={(e) => onImageSourceChange(e.target.value)}
              placeholder="z.B. PeterKlein / Shutterstock"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bildquelle-URL
            </label>
            <Input
              value={imageSourceUrl}
              onChange={(e) => onImageSourceUrlChange(e.target.value)}
              placeholder="https://www.shutterstock.com/de/image-photo/..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bildunterschrift
            </label>
            <Input
              value={imageCaption}
              onChange={(e) => onImageCaptionChange(e.target.value)}
              placeholder="Beschreibung des Bildes"
            />
          </div>
        </div>
      )}

      {/* KI-generiert */}
      {imageType === 'ki' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bild-Prompt *
            </label>
            <Textarea
              value={imagePrompt}
              onChange={(e) => onImagePromptChange(e.target.value)}
              placeholder="Beschreibe das gewünschte Bild für den Artikel …"
              rows={4}
            />
          </div>

          <div className="flex flex-col items-center space-y-2">
            <Button
              onClick={generateImage}
              disabled={isLoading || !imagePrompt.trim()}
              className="px-8"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">KI-Vorschläge werden generiert...</span>
                </>
              ) : (
                <>
                  <CpuChipIcon className="w-4 h-4 mr-2" />
                  KI-Vorschläge generieren
                </>
              )}
            </Button>
            <p className="text-sm text-gray-500">
              ⏱️ Die Generierung kann 90-120 Sekunden dauern
            </p>
          </div>

          {generatedImages.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Generierte Bilder ({generatedImages.length})
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {generatedImages.map((image, index) => (
                  <div
                    key={index}
                    className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                      image.selected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => selectImage(index)}
                  >
                    <img
                      src={image.url}
                      alt={`KI-generiertes Bild ${index + 1}`}
                      className="w-full h-32 object-contain"
                    />
                    <div className="p-2 bg-white">
                      <p className="text-xs text-gray-600 truncate">
                        {image.caption.substring(0, 50)}...
                      </p>
                      {image.selected && (
                        <div className="flex items-center mt-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                          <span className="text-xs text-blue-600 font-medium">Ausgewählt</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {selectedImageIndex >= 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bildunterschrift für ausgewähltes Bild
                  </label>
                  <Input
                    value={imageCaption}
                    onChange={(e) => onImageCaptionChange(e.target.value)}
                    placeholder="Bildunterschrift hinzufügen"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Redaktion */}
      {imageType === 'redaktion' && (
        <div className="p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
          <div className="flex items-center">
            <UserGroupIcon className="w-5 h-5 text-yellow-600 mr-2" />
            <div>
              <p className="text-yellow-800 text-sm font-medium">
                Redaktionelle Bildauswahl
              </p>
              <p className="text-yellow-700 text-sm">
                Die Redaktion wird ein passendes Bild für deinen Artikel auswählen.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

